import * as RadixPopover from '@radix-ui/react-popover';
import type * as React from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';

import Content from './components/content/content';
import Input from './components/input';

export type SelectAll = {
  label?: string;
  onSelect?: () => void;
  defaultValue?: boolean;
  isAllSelected?: boolean;
};

export type Option = {
  label: string;
  value: string;
  /** Shown after the label, in the list and on the chip */
  suffix?: React.ReactNode;
  toggleOption?: (value: string) => void;
};

export type MultiSelectProps = {
  className?: string;
  disabled?: boolean;
  errorMessage?: string;
  hasError?: boolean;
  hideSelectAll?: boolean;
  onChange: (value: string[]) => void;
  options: Option[];
  selectedOptions?: Option[];
  onSearchChange?: (search: string) => void;
  emptyState?: React.ReactNode;
  isLoading?: boolean;
  placeholder?: string;
  selectAll?: SelectAll;
  value?: string[];
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  alwaysOpen?: boolean;
};

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      onBlur,
      onFocus,
      options,
      selectedOptions,
      onSearchChange,
      emptyState,
      isLoading,
      onChange,
      value = [],
      placeholder = 'Select options',
      className,
      hideSelectAll = false,
      selectAll = {
        label: 'Select All',
        onSelect: () => {
          // No-op function for default select all behavior
        },
        defaultValue: false,
        isAllSelected: false,
      },
      hasError = false,
      errorMessage,
      disabled = false,
      alwaysOpen = false,
    },
    ref,
  ) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleOpenChange = (open: boolean) => {
      setIsPopoverOpen(open);
      if (!open) onSearchChange?.('');
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...value];
        newSelectedValues.pop();
        onChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const newSelectedValues = value.includes(option) ? value.filter(value => value !== option) : [...value, option];
      onChange(newSelectedValues);
    };

    const handleSelectAll = () => {
      const allValues = options.map(option => option.value);
      const newSelectedValues = value.length === options.length ? [] : allValues;
      onChange(newSelectedValues);
      if (selectAll?.onSelect) {
        selectAll.onSelect();
      }
    };

    useEffect(() => {
      if (selectAll) {
        selectAll.isAllSelected = value.length === options.length;
      }
    }, [value, options.length, selectAll]);

    useEffect(() => {
      if (selectAll?.defaultValue) {
        const allValues = options.map(option => option.value);
        onChange(allValues);
      }
    }, [selectAll?.defaultValue, options, onChange]);

    const handleButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        handleOpenChange(!isPopoverOpen);
      }
    };

    return (
      <RadixPopover.Root open={alwaysOpen || isPopoverOpen} onOpenChange={handleOpenChange}>
        <div className="flex flex-col" ref={wrapperRef}>
          <Input
            className={className}
            disabled={disabled}
            hasError={hasError}
            isPopoverOpen={isPopoverOpen}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={handleButtonKeyDown}
            options={selectedOptions ?? options}
            placeholder={placeholder}
            ref={ref}
            selectedValues={value}
            toggleOption={toggleOption}
          />
          {hasError && <div className="mt-1 text-error text-body-2">{errorMessage}</div>}
        </div>
        <RadixPopover.Portal container={wrapperRef.current}>
          <Content
            onOpenChange={handleOpenChange}
            open={alwaysOpen || isPopoverOpen}
            options={options.map(option => ({
              ...option,
              toggleOption: () => toggleOption(option.value),
            }))}
            value={value}
            handleInputKeyDown={handleInputKeyDown}
            selectAll={{
              ...selectAll,
              onSelect: handleSelectAll,
            }}
            hideSelectAll={hideSelectAll}
            onSearchChange={onSearchChange}
            emptyState={emptyState}
            isLoading={isLoading}
          />
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  },
);

export default MultiSelect;
